import React from 'react';

const LivingPage = () => {
  return (
    <div className="bg-background text-on-background font-body antialiased">
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative px-8 pt-24 pb-32 max-w-[1440px] mx-auto overflow-hidden">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-7">
              <h1 className="text-6xl md:text-7xl font-bold tracking-tighter text-on-surface mb-8 leading-[1.1]">
                Your <span className="text-primary">Neighborhood</span> Guide
              </h1>
              <p className="text-xl text-on-surface-variant max-w-xl leading-relaxed mb-12">
                Navigating local life with authority. We’ve curated the essential nodes of your daily routine so you can focus on living, not just searching.
              </p>
              <div className="flex flex-wrap gap-4">
                <span className="bg-primary-fixed text-on-primary-fixed px-4 py-2 rounded-full text-sm font-semibold">Verified by Curators</span>
                <span className="bg-surface-container-high text-on-surface-variant px-4 py-2 rounded-full text-sm font-medium">Daily Updates</span>
              </div>
            </div>
            <div className="hidden lg:block col-span-5 relative">
              <div className="rounded-[2.5rem] overflow-hidden aspect-square shadow-2xl">
                <img 
                  className="w-full h-full object-cover" 
                  alt="Bucharest morning market" 
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuD2A_Q8VGfgfsJ6V-sz-GGubeW1Nt9ckHMrO7qH7NE52eeUwpa91IFKiTtrqGy1z7wr3AYLluzUUnXF2z_dcnQfxrBns3Y6rEWu5rtkAfxQX6UmRoY-kJga11kA5R_18_J_-OL4l1dr0QIfSXDk6LvOqDCfDZ0A3UPKKsiF0w7-fsjdWEbTa_FEEnfkUZrYHsrcIx8E6i81vSEm7OHswfoX46SndoWqKuijRs9Q2RI5ozIcXKmCq3Tm_G1PoHaQYvNjpJ_pTPePR-8"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-surface-container-lowest p-6 rounded-2xl shadow-xl max-w-[200px]">
                <p className="text-primary font-bold text-lg mb-1">98%</p>
                <p className="text-xs text-on-surface-variant">Community trust rating for local recommendations.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Groceries Section */}
        <section className="bg-surface-container-low py-32 px-8">
          <div className="max-w-[1440px] mx-auto">
            <div className="flex justify-between items-end mb-16">
              <div className="max-w-2xl">
                <h2 className="text-4xl font-bold tracking-tight mb-4">The Pantry Essentials</h2>
                <p className="text-on-surface-variant text-lg">Curated markets where quality meets local tradition. Hand-picked for the discerning home cook.</p>
              </div>
              <button className="text-primary font-semibold flex items-center gap-2 group">
                View All Markets
                <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Market Card 1 */}
              <div className="bg-surface-container-lowest p-4 rounded-[2rem] shadow-sm group transition-transform hover:-translate-y-2">
                <div className="relative rounded-[1.5rem] overflow-hidden aspect-[4/3] mb-6">
                  <img 
                    className="w-full h-full object-cover" 
                    alt="Fresh organic produce" 
                    src="https://lh3.googleusercontent.com/aida-public/AB6AXuAT7vsgEDDvGz_GFSbEDSp0x23ED1yLg2QHJfVpT_ZjobvSucK7gEnHbeaUsKaTecHYhyXPPovr4Tuw7iSGBmzHDXQHFBFe-VWO6CH2v6qbQLM4DY6Vga2y5pPXM8ihMlKGMj4X4F2rq2UlVxl3i5_8gJpcpBUQuvoK23LK4uyggc9XhH3PrZxmRNFH9NWYnxA5ldbRcACbeIKLiC3P5s5D2JzxzlFA1SvGkoIORKP8LGvSX8QW1E7SOlo6U7fGMcAKzC1eOe6lBP8"
                  />
                  <div className="absolute top-4 right-4 bg-primary text-white px-4 py-1.5 rounded-full text-xs font-bold">Curator's Choice</div>
                </div>
                <div className="px-4 pb-4">
                  <h3 className="text-2xl font-bold mb-2">Piața Obor Market</h3>
                  <p className="text-on-surface-variant mb-6 text-sm">Best for organic produce and authentic regional honey. A true local landmark with unparalleled freshness.</p>
                  <div className="flex items-center justify-between border-t border-outline-variant/10 pt-4">
                    <span className="text-xs font-bold text-primary tracking-widest uppercase">Organic • Local</span>
                    <span className="text-xs text-on-surface-variant">0.8 miles away</span>
                  </div>
                </div>
              </div>
              {/* Market Card 2 */}
              <div className="bg-surface-container-lowest p-4 rounded-[2rem] shadow-sm group transition-transform hover:-translate-y-2">
                <div className="relative rounded-[1.5rem] overflow-hidden aspect-[4/3] mb-6">
                  <img 
                    className="w-full h-full object-cover" 
                    alt="Artisan boutique store" 
                    src="https://lh3.googleusercontent.com/aida-public/AB6AXuDu7LbWi2l8z3hocbq5AlELIa-6m5jScUZGvVry1DeWEAGAxUUS2CjGcarnhzBf0lVEen2aSBkSr9BzCSUBBSjQXwGaZL6vO1yXfLgQ-Hqy6PilwiX0eZF_7Jv9hXynsReTWTuK26kung2GM64lk5bCAYzEmlUXaggqGLWuJuqYd-XvlZvMUJ88LNX0Ua34otoQ8EA02yNymBgYemvKzMC9V8uwkoogw0y58AxGl8pRfv_spp8F4qDmNnKwHJFbSV76b_EGX5s8-yY"
                  />
                </div>
                <div className="px-4 pb-4">
                  <h3 className="text-2xl font-bold mb-2">Artisan Larder</h3>
                  <p className="text-on-surface-variant mb-6 text-sm">Specialty imports and local cheeses. Ideal for finding that rare ingredient or a curated weekend gift basket.</p>
                  <div className="flex items-center justify-between border-t border-outline-variant/10 pt-4">
                    <span className="text-xs font-bold text-secondary tracking-widest uppercase">Gourmet • Imports</span>
                    <span className="text-xs text-on-surface-variant">1.4 miles away</span>
                  </div>
                </div>
              </div>
              {/* Market Card 3 */}
              <div className="bg-surface-container-lowest p-4 rounded-[2rem] shadow-sm group transition-transform hover:-translate-y-2">
                <div className="relative rounded-[1.5rem] overflow-hidden aspect-[4/3] mb-6">
                  <img 
                    className="w-full h-full object-cover" 
                    alt="Artisanal bakery" 
                    src="https://lh3.googleusercontent.com/aida-public/AB6AXuA0zjxPEoNi5zeaeA_NpSxLTSBNQcgvKPIZXmjE7SHyrnfO9p4HfYXcVyGyBW92ti_fbyAJuz_ZyVKdj1K0h7LJ6h3UGotu0qze6WI0aItFEszZSlhggWRNvQJzmo6Rl99Xt-J8bEmSqgTQCiFNi2t0auu0UuYHcG5_NDsS2VeMMQvpxT_A6yIy76NI8ekJ1O_OecPiiIrJPfCzspsAfrvwjjARj9nUtcPMR6ggGDdeXupxImVUWd5hUTPAR6O0QJlxpbWBdSFIDLE"
                  />
                  <div className="absolute top-4 right-4 bg-primary text-white px-4 py-1.5 rounded-full text-xs font-bold">Curator's Choice</div>
                </div>
                <div className="px-4 pb-4">
                  <h3 className="text-2xl font-bold mb-2">Grain & Hearth</h3>
                  <p className="text-on-surface-variant mb-6 text-sm">Traditional sourdough and seasonal pastries. The daily morning ritual for those who value heritage grains.</p>
                  <div className="flex items-center justify-between border-t border-outline-variant/10 pt-4">
                    <span className="text-xs font-bold text-primary tracking-widest uppercase">Bakery • Tradition</span>
                    <span className="text-xs text-on-surface-variant">0.3 miles away</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pharmacy & Health Section */}
        <section className="py-32 px-8 max-w-[1440px] mx-auto">
          <div className="grid grid-cols-12 gap-12 items-center">
            <div className="col-span-12 lg:col-span-5 order-2 lg:order-1">
              <div className="space-y-6">
                <span className="text-secondary font-bold tracking-[0.2em] uppercase text-xs">Well-being Essentials</span>
                <h2 className="text-5xl font-bold tracking-tight text-on-surface leading-tight">Trust Your Health to the Experts.</h2>
                <p className="text-lg text-on-surface-variant leading-relaxed">
                  We identify pharmacies that prioritize personal consultation and consistent stock of international standards. Reliability is our only metric.
                </p>
                <div className="space-y-4 mt-8">
                  <div className="flex gap-4 p-6 rounded-2xl bg-surface-container-low">
                    <span className="material-symbols-outlined text-primary text-3xl">medical_services</span>
                    <div>
                      <h4 className="font-bold text-lg mb-1">Farmacia Tei</h4>
                      <p className="text-sm text-on-surface-variant">Renowned for extensive inventory and professional consultation. Open 24/7 for urgent needs.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 p-6 rounded-2xl bg-surface-container-low">
                    <span className="material-symbols-outlined text-secondary text-3xl">verified_user</span>
                    <div>
                      <h4 className="font-bold text-lg mb-1">Sensiblu Curated</h4>
                      <p className="text-sm text-on-surface-variant">Modern health essentials with a focus on holistic wellness and premium skincare.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-7 order-1 lg:order-2">
              <div className="relative rounded-[3rem] overflow-hidden aspect-[16/10] shadow-2xl">
                <img 
                  className="w-full h-full object-cover" 
                  alt="Apothecary details" 
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuAg4tRZ_3c5aBYWOGq_Bm9PZZGRkccnv8j0zNZ4vTzU4q6le2-g7zQBNzjSKIgcyOMlIItnkjqxqH-4RLFz-CDcKAy3jc5tDQjfr53Ef1ETRaVJOaZhJw-qv3W1fhg-wVR6KXxZ8fgKy8ZbvZg2C3uNvb6p5ngsYy30qtcstIE4spzwqsFFx0fYZYWdc0A3eblQ7WQAt4Ic_KHe2ZtM8DZGQOyr8U3i4wsq4jERxVFV4qSgVQb9rvrVPwOO87TdgIOwYlHG2kjzPq0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Romanian Culture Tips Section (Editorial) */}
        <section className="px-8 pb-32">
          <div className="bg-primary text-on-primary rounded-[3rem] p-12 md:p-24 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-1/3 h-full bg-primary-container/20 -skew-x-12 translate-x-20"></div>
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-8">Romanian Culture:<br/>An Insider’s Perspective</h2>
                <p className="text-on-primary-container text-lg max-w-lg mb-12">
                  To live like a local, one must understand the silent rhythms of the city. These are the nuances that transform a visitor into a neighbor.
                </p>
                <div className="inline-flex items-center gap-4 bg-white/10 backdrop-blur-md px-6 py-4 rounded-2xl">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary-fixed">
                    <img 
                      className="w-full h-full object-cover" 
                      alt="Andrei Popescu" 
                      src="https://lh3.googleusercontent.com/aida-public/AB6AXuBKiLvFhSZn_OkU5zNn7sPPEATyM0y0A-4NccE0iANCijkT47_q5EIJ5vPUInbHfQhx4_drht9e_rUZjPJCIEdFfZqOQhlT0iOtKg8f2d35936YwyS1hDJbzbycOIsx9hkiUfIh4a3YanU26y8tyRQDCLRx41F15I2fyuVr5zg61L_oO9LgIdbboY0WKeti_bZaVdhRmqjJHa99TFGGLEz5WGodZGVNO12MuGYEbkxhFh06gttL-Hs-Bfn7XaUtS91S3tprhxYIsQI"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-bold">Andrei Popescu</p>
                    <p className="text-xs opacity-70">Cultural Heritage Curator</p>
                  </div>
                </div>
              </div>
              <div className="grid gap-6">
                <div className="bg-white/5 backdrop-blur-lg p-8 rounded-3xl border border-white/10 hover:bg-white/10 transition-colors">
                  <h3 className="text-xl font-bold mb-3 flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary-fixed">coffee</span>
                    The Art of the Morning Coffee
                  </h3>
                  <p className="text-sm leading-relaxed opacity-90">In Romania, coffee isn't a commute companion; it's a social anchor. Dedicate at least 30 minutes. Even the smallest "cafenea" is a place to observe the local pulse before the day begins.</p>
                </div>
                <div className="bg-white/5 backdrop-blur-lg p-8 rounded-3xl border border-white/10 hover:bg-white/10 transition-colors">
                  <h3 className="text-xl font-bold mb-3 flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary-fixed">shopping_basket</span>
                    Sunday Market Etiquette
                  </h3>
                  <p className="text-sm leading-relaxed opacity-90">Never rush a vendor. Piața visits are about the 'tasting' (degustare). Ask about the region of the honey or the harvest of the zacuscă. Respect for the source earns you the best selection.</p>
                </div>
                <div className="bg-white/5 backdrop-blur-lg p-8 rounded-3xl border border-white/10 hover:bg-white/10 transition-colors">
                  <h3 className="text-xl font-bold mb-3 flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary-fixed">temple_hindu</span>
                    Hospitality Traditions
                  </h3>
                  <p className="text-sm leading-relaxed opacity-90">If invited into a home, 'poftă bună' (enjoy your meal) is more than a phrase—it's a shared acknowledgement of the host's effort. Bringing a small token, like local flowers or chocolates, is always remembered.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LivingPage;
